import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import AuthLayout from "@/components/AuthLayout";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/app/dashboard";

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(identifier.trim(), password);
      toast.success("Signed in.");
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      eyebrow="Sign In"
      title="Welcome back."
      subtitle="Access your workspace with your email or phone number."
    >
      <form
        data-testid="login-form"
        onSubmit={onSubmit}
        className="flex flex-col gap-6"
        noValidate
      >
        <div>
          <label
            htmlFor="identifier"
            className="block text-sm font-medium text-slate-900 mb-2"
          >
            Email or phone number
          </label>
          <input
            data-testid="login-identifier-input"
            id="identifier"
            name="identifier"
            type="text"
            autoComplete="username"
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="you@company.com or +14155550101"
            className="w-full h-12 px-4 rounded-sm bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-slate-900"
            >
              Password
            </label>
            <Link
              data-testid="login-forgot-password-link"
              to="/forgot-password"
              className="text-sm text-[#002FA7] hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              data-testid="login-password-input"
              id="password"
              name="password"
              type={showPwd ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full h-12 px-4 pr-24 rounded-sm bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowPwd((s) => !s)}
              data-testid="login-toggle-password-visibility"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-[#002FA7] transition-colors"
            >
              {showPwd ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {error && (
          <div
            data-testid="login-error"
            role="alert"
            className="text-sm text-[#D92D20] border border-[#D92D20]/30 bg-[#D92D20]/5 px-3 py-2 rounded-sm"
          >
            {error}
          </div>
        )}

        <button
          data-testid="login-submit-button"
          type="submit"
          disabled={submitting}
          className="h-12 w-full bg-[#002FA7] text-white font-medium rounded-sm hover:bg-[#002280] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>

        <p className="text-sm text-slate-600">
          No account yet?{" "}
          <Link
            data-testid="login-goto-register-link"
            to="/register"
            className="text-[#002FA7] font-medium hover:underline"
          >
            Create one
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
